import { hydrateRoot, createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter } from "react-router-dom";
import { hydrate as hydrateQuery } from "@tanstack/react-query";
import { AppShell, makeQueryClient } from "./App";
import { ROUTER_BASENAME } from "./lib/basePath";
import { looksLikeChunkLoadError, reloadOnceForChunkError } from "./lib/chunkReload";
import { installDomTranslateGuard } from "./lib/domTranslateGuard";
import "./index.css";
import "./i18n";

// Install before the first render: browser translation (Google/Safari Translate)
// swaps text nodes out from under React, and without this guard the next
// reconciliation throws "removeChild … not a child of this node" and crashes the
// subtree to the ErrorBoundary. Runs once; browser-only (no-op under SSR).
installDomTranslateGuard();

const queryClient = makeQueryClient();

// After a deploy, an open page references old chunk hashes; navigating to a lazy
// route then fails to import them. Reload once to pull the fresh assets (see
// lib/chunkReload). These global handlers cover the cases that DON'T reach an
// <ErrorBoundary>: the Vite preload error, and rejected imports that escape as
// unhandled rejections / window errors. React.lazy failures caught by an
// ErrorBoundary are handled in the boundary itself.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", () => reloadOnceForChunkError());

  window.addEventListener("unhandledrejection", (e) => {
    const msg = String((e.reason && (e.reason.message || e.reason)) || "");
    if (looksLikeChunkLoadError(msg) && reloadOnceForChunkError()) e.preventDefault();
  });
  window.addEventListener("error", (e) => {
    if (looksLikeChunkLoadError(String(e.message || ""))) reloadOnceForChunkError();
  });
}

// Seed the cache with data the server prefetched (gallery describe, etc.) so the
// first client render matches SSR and doesn't refetch.
declare global {
  interface Window {
    __PH_RQ__?: unknown;
  }
}
if (typeof window !== "undefined" && window.__PH_RQ__) {
  try {
    hydrateQuery(queryClient, window.__PH_RQ__);
  } catch {
    /* ignore malformed state */
  }
}

const app = (
  <HelmetProvider>
    <BrowserRouter basename={ROUTER_BASENAME}>
      <AppShell queryClient={queryClient} />
    </BrowserRouter>
  </HelmetProvider>
);

const container = document.getElementById("root")!;

// If the server rendered real markup, hydrate it; if the server fell back to an
// empty shell (SSR error / CSR fallback), mount fresh instead.
if (container.hasChildNodes()) {
  hydrateRoot(container, app);
} else {
  createRoot(container).render(app);
}
